import {useContext} from 'react';

import {CustomizeJourneyContext} from '@amblique/king-living/app/context/customize-journey-context';

/**
 * Consumer hook for the CustomizeJourneyContext.
 *
 * Must be used within a CustomizeJourneyProvider.
 *
 * Returns:
 * - committedSelection: persisted selection state (mode, productId, selections, sourceSetId, deviatedFromSet)
 * - flyoutDraftState: temporary flyout editing state (isOpen, editingProductId, draftSelections, isDeviatingFromSet)
 * - inlineSelectionsDraft: live CustomizeStepsInline draft while that shell is mounted; null otherwise
 * - setInlineSelectionsDraft: setter used by CustomizeStepsInline only
 * - setCards: array of SetCard objects from linked products
 * - setProductsMap: map of productId → full raw SCAPI product for each linked set product
 * - isLoadingSetCards: whether linked products are still being fetched
 * - selectSet(setId): select a pre-configured set
 * - editSet(setId): select a set AND open the flyout for editing in one atomic operation
 * - switchToCustomizeInFlyout(): switch flyout from a pre-defined set back to free customize mode
 * - openFlyout(editingProductId): open flyout for editing, seeding draft from committed selections
 * - updateDraftSelection(stepId, value): update a step selection in the flyout draft
 * - commitFlyout(): commit draft to committed state and close flyout
 * - cancelFlyout(): discard draft and close flyout without changing committed state
 * - commitInlineSelections(selections): commit inline draft directly to committedSelection
 * - resetJourney(): reset all state to initial
 *
 * Panel header info (step title, back navigation) is NOT part of this context.
 * Each shell component (ConfigurationSelector, CustomizeStepsInline) manages it as local state
 * and receives it via the onHeaderChange callback from CustomizeStepsContent.
 *
 * @returns {import('../context/customize-journey-context').CustomizeJourneyContext}
 */
export function useCustomizeJourney() {
  const context = useContext(CustomizeJourneyContext);

  if (!context) {
    throw new Error('useCustomizeJourney must be used within a CustomizeJourneyProvider');
  }

  return context;
}
